import { fromHex } from "@cosmjs/encoding";
import { computePublicKey } from "@ethersproject/signing-key";
import { getAddress } from "@ethersproject/address";
import { Connector, ChainInfo } from "@funcx/connector";
import {
  testnetEthereumFxevm,
  mainnetEthereumFxevm,
  cosmosMarginxChain,
} from "@funcx/connect-configs";
import { CosmosJsonProvider } from "@funcx/cosmos-json-provider";
import { EthereumJsonProvider } from "@funcx/ethereum-json-provider";
import QRCode from "qrcode";
import {
  fxDexTxConfig,
  SigningFxClient,
  hexAddressToBech32,
  bech32ToHexAddress,
} from "@functionx_io/fx-js-sdk";
import {
  ethers,
  providers,
  Contract,
  BigNumber as EthersBigNumber,
} from "ethers";
import erc20Fragment from "../../connect/abi/erc20";
import { Format } from "@/tools/filter";
import CrossChainContracts from "../../connect/func/CrossChainContracts";
import { SpecialTreatment } from "@/tools/filter";
import { coins } from "@cosmjs/amino";
import { $t } from "@/i18n";
import MarginxTxContract from "../../connect/func/MarginxTxContract";
import WithdrawRewardsContracts from "../../connect/func/WithdrawRewardsContracts";
import ConfigureInfo from "@/connect/configureInfo";
import api from "@/service/api";
import { marginxSuggestChainsBase } from "@/connect/suggestChains";
import { Notification } from "element-ui";
import { TxRaw } from "cosmjs-types/cosmos/tx/v1beta1/tx";
import { ExtendedSecp256k1Signature, Secp256k1Signature } from "@cosmjs/crypto";
import { SignTypedDataVersion } from "@metamask/eth-sig-util";
import { bufferToHex, fromRpcSig, setLengthLeft } from "@ethereumjs/util";
import { TypedDataUtils } from "@metamask/eth-sig-util/dist/sign-typed-data";
import { ecdsaRecover } from "ethereum-cryptography/secp256k1-compat";

import createTypedData from "../../lib/payload/createTypedData";
import Long from "long";

const ethereumFxevm =
  process.env.VUE_APP_ENV === "mainnet"
    ? mainnetEthereumFxevm
    : testnetEthereumFxevm;

export const marginxChainsData = new ChainInfo({
  chainId: `marginx:${ConfigureInfo.Marginx.chainId}`,
  chainName: ConfigureInfo.Marginx.chainName,
  rpc: ConfigureInfo.Marginx.jsonRpcUrl,
  rest: ConfigureInfo.Marginx.restUrl,
  ...marginxSuggestChainsBase,
});

export const ethereumMxevm = new ChainInfo({
  chainId: [
    `eip155:${ConfigureInfo.Marginx.evmChainId}`,
    `marginx:${ConfigureInfo.Marginx.chainId}`,
  ],
  chainName: ConfigureInfo.Marginx.chainName,
  alias: "mxevm",
  decimals: 18,
  symbol: "cusd",
  rpc: ConfigureInfo.Marginx.web3Url,
  explorer: "",
  appConfig: {
    session: 39777,
    target: "mxevm",
    methods: [
      "functionx_signDirect",
      "functionx_signAmino",
      "functionx_getAccounts",
    ],
  },
  suggest: {
    ...marginxSuggestChainsBase.suggest,
    bip44: { coinType: 60 },
  },
});

function directionFromJSON(object) {
  switch (object) {
    case 0:
      return "BOTH";
    case 1:
      return "BUY";
    case 2:
      return "SELL";
    case 3:
      return "MarketBuy";
    case 4:
      return "MarketSell";
    default:
      return "UNRECOGNIZED";
  }
}

function omitDefault(input) {
  if (typeof input === "string") {
    return input === "" ? undefined : input;
  }

  if (typeof input === "number") {
    return input === 0 ? undefined : input;
  }

  if (Long.isLong(input)) {
    return input.isZero() ? undefined : input;
  }

  throw new Error(`Got unsupported type '${typeof input}'`);
}

function recoverPublicKeys(messageHash, signature) {
  const sigParams = fromRpcSig(signature);
  return ecrecovers(messageHash, sigParams.v, sigParams.r, sigParams.s);
}

function ecrecovers(msgHash, v, r, s) {
  const signature = Buffer.concat(
    [setLengthLeft(r, 32), setLengthLeft(s, 32)],
    64
  );
  // eslint-disable-next-line no-undef
  const result = v - BigInt(27);
  const senderPubKey = ecdsaRecover(
    signature,
    Number(result.toString()),
    msgHash
  );
  return senderPubKey;
}

async function ethSignTypedData(client, chainId, account, msg) {
  console.log("ethSignTypedData-msg", msg);
  const signature = await client.request({
    chainId: chainId,
    method: "eth_signTypedData",
    params: [bech32ToHexAddress(account), JSON.stringify(msg)],
  });

  const messageHash = TypedDataUtils.eip712Hash(msg, SignTypedDataVersion.V4);

  const recoverPublicKeyVal = recoverPublicKeys(messageHash, signature);

  return { signature, recoverPublicKeyVal };
}

async function ethSignTypedDataTx(provider, transaction, signature) {
  const { bodyBytes, authInfoBytes } = await provider.parseTxBytes(transaction);

  signature = fromHex(
    signature.startsWith("0x") ? signature.slice(2) : signature
  );

  signature[signature.length - 1] = 0;

  let secp256k1Signature;
  if (signature.length === 65)
    secp256k1Signature = ExtendedSecp256k1Signature.fromFixedLength(signature);
  else secp256k1Signature = Secp256k1Signature.fromFixedLength(signature);

  const signatureBytes = new Uint8Array([
    ...secp256k1Signature.r(32),
    ...secp256k1Signature.s(32),
  ]);

  const txRaw = TxRaw.fromPartial({
    bodyBytes,
    authInfoBytes,
    signatures: [signatureBytes],
  });

  return TxRaw.encode(txRaw).finish();
}

async function ethSignTypedDataAndBroadcast(
  account,
  provider,
  client,
  signDoc,
  msg
) {
  const { signature, recoverPublicKeyVal } = await ethSignTypedData(
    client,
    ethereumMxevm.chainId,
    account,
    msg
  );

  const transaction = await provider.populateTransaction({
    messages: signDoc.msgs,
    memo: "",
    fee: signDoc.fee,
    publicKey: bufferToHex(recoverPublicKeyVal).slice(2),
    algo: "eth_secp256k1",
    address: account,
    mode: "direct",
  });

  console.log("transaction:", transaction);

  const signTx = await ethSignTypedDataTx(provider, transaction, signature);

  console.log("ethSignTypedDataAndBroadcast-sign", signTx);
  try {
    const result = await provider.broadcastTx(signTx);
    console.log("ethSignTypedDataAndBroadcast-result", result);

    return result;
  } catch (error) {
    console.error("error", error);

    throw error;
  }
}

export function getProvider(rpc) {
  return new ethers.providers.JsonRpcProvider(rpc);
}

export async function hexlifySignTransaction(buildTx) {
  return providers.JsonRpcProvider.hexlifyTransaction(buildTx, {
    from: true,
  });
}
export function withContract(
  addressOrName,
  contractInterface,
  signerOrProvider
) {
  return new Contract(addressOrName, contractInterface, signerOrProvider);
}
export function getProviderMarginxTxContract(rpc, account) {
  const provider = getProvider(rpc);
  const contract = new MarginxTxContract(provider, account);
  return { provider, contract };
}
export const buildContractsTransfer = {
  claim: async (params) => {
    console.log("claim-data", params);
    const provider = getProvider(ConfigureInfo.FxEvm.rpc);
    const contract = new WithdrawRewardsContracts(
      provider,
      params.account,
      ConfigureInfo.WithdrawRewardsAddr["FX"]
    );
    const buildTx = await contract.populateTransaction.claim(
      params.amount,
      params.tokenType
    );
    buildTx.value = EthersBigNumber.from("0x0");
    return await provider.getSigner(buildTx.from).populateTransaction(buildTx);
  },
  crossChain: async (data) => {
    const provider = getProvider(data.web3Url);
    const contractAddress = "0x0000000000000000000000000000000000001004";
    const contract = new CrossChainContracts(
      provider,
      data.account,
      contractAddress
    );
    console.log(
      "crossChain",
      data.value.token.denom === "FX"
        ? "0x0000000000000000000000000000000000000000"
        : data.tokenAddr,
      data.recipient,
      EthersBigNumber.from(data.amount),
      EthersBigNumber.from(data.fee),
      data.target,
      ""
    );
    const buildTx = await contract.populateTransaction.crossChain(
      data.value.token.denom === "FX"
        ? "0x0000000000000000000000000000000000000000"
        : data.tokenAddr,
      data.recipient,
      EthersBigNumber.from(data.amount),
      EthersBigNumber.from(data.fee),
      data.target,
      ""
    );

    buildTx.value =
      data.value.token.denom === "FX"
        ? EthersBigNumber.from(data.amount).add(data.fee)
        : EthersBigNumber.from("0x0");

    return await provider.getSigner(buildTx.from).populateTransaction(buildTx);
  },
};
export const buildMetamaskTransfer = {
  "/fx.dex.v1.MsgCreateOrder": async (data) => {
    const { provider, contract } = getProviderMarginxTxContract(
      data.web3Url,
      data.account
    );
    const buildTx = await contract.populateTransaction.CreateOrder(
      data.value.owner,
      data.value.pairId,
      EthersBigNumber.from(data.value.direction),
      EthersBigNumber.from(data.value.price),
      EthersBigNumber.from(data.value.baseQuantity),
      EthersBigNumber.from(data.value.leverage.toString())
    );
    buildTx.type = 0;

    return await provider.getSigner(buildTx.from).populateTransaction(buildTx);
  },
  "/fx.dex.v1.MsgCancelOrder": async (data) => {
    const { provider, contract } = getProviderMarginxTxContract(
      data.web3Url,
      data.account
    );
    const buildTx = await contract.populateTransaction.CancelOrder(
      data.value.owner,
      data.value.orderId
    );
    buildTx.type = 0;

    return await provider.getSigner(buildTx.from).populateTransaction(buildTx);
  },
  "/fx.dex.v1.MsgClosePosition": async (data) => {
    const { provider, contract } = getProviderMarginxTxContract(
      data.web3Url,
      data.account
    );
    const buildTx = await contract.populateTransaction.ClosePosition(
      data.value.owner,
      data.value.pairId,
      EthersBigNumber.from(data.value.positionId),
      EthersBigNumber.from(data.value.price),
      EthersBigNumber.from(data.value.baseQuantity),
      data.value.fullClose,
      data.value.marketClose
    );
    buildTx.type = 0;

    return await provider.getSigner(buildTx.from).populateTransaction(buildTx);
  },
  "/fx.dex.v1.MsgAddMargin": async (data) => {
    const { provider, contract } = getProviderMarginxTxContract(
      data.web3Url,
      data.account
    );
    const buildTx = await contract.populateTransaction.AddMargin(
      data.value.owner,
      data.value.pairId,
      EthersBigNumber.from(data.value.positionId),
      EthersBigNumber.from(data.value.margin)
    );
    buildTx.type = 0;

    return await provider.getSigner(buildTx.from).populateTransaction(buildTx);
  },
  "/fx.ibc.applications.transfer.v1.MsgTransfer": async (data) => {
    const { provider, contract } = getProviderMarginxTxContract(
      data.web3Url,
      data.account
    );
    const buildTx = await contract.populateTransaction.IBCTransfer(
      data.value.sourcePort,
      data.value.sourceChannel,
      data.value.sender,
      data.value.receiver,
      EthersBigNumber.from("0"),
      EthersBigNumber.from(data.value.timeoutTimestamp.toString()),
      data.value.router,
      data.value.token.denom,
      EthersBigNumber.from(data.value.token.amount),
      data.value.fee.denom,
      EthersBigNumber.from(data.value.fee.amount)
    );
    buildTx.type = 0;

    return await provider.getSigner(buildTx.from).populateTransaction(buildTx);
  },
};

export const EIP712HandleSpecial = {
  "/fx.dex.v1.MsgCreateOrder": async (msgs) => {
    const toAminoMsg = fxDexTxConfig.options.aminoTypes.toAmino(msgs);
    toAminoMsg.type = "/fx.dex.v1.MsgCreateOrder";
    toAminoMsg.value.direction = directionFromJSON(msgs.value.direction);
    return toAminoMsg;
  },
  "/fx.dex.v1.MsgCancelOrder": async (msgs) => {
    const toAminoMsg = fxDexTxConfig.options.aminoTypes.toAmino(msgs);
    toAminoMsg.type = "/fx.dex.v1.MsgCancelOrder";
    return toAminoMsg;
  },
  "/fx.dex.v1.MsgClosePosition": async (msgs) => {
    const toAminoMsg = fxDexTxConfig.options.aminoTypes.toAmino(msgs);
    toAminoMsg.type = "/fx.dex.v1.MsgClosePosition";
    return toAminoMsg;
  },
  "/fx.dex.v1.MsgAddMargin": async (msgs) => {
    const toAminoMsg = fxDexTxConfig.options.aminoTypes.toAmino(msgs);
    toAminoMsg.type = "/fx.dex.v1.MsgAddMargin";
    return toAminoMsg;
  },
  "/fx.ibc.applications.transfer.v1.MsgTransfer": async (msgs) => {
    console.log("MsgTransfer", msgs);
    const toAminoMsg = fxDexTxConfig.options.aminoTypes.toAmino(msgs);
    toAminoMsg.value.timeout_height = {
      revision_height: "0",
      revision_number: "0",
    };
    toAminoMsg.value.router = toAminoMsg.value.router
      ? toAminoMsg.value.router
      : "";
    toAminoMsg.value.memo = "";

    toAminoMsg.type = "/fx.ibc.applications.transfer.v1.MsgTransfer";
    return toAminoMsg;
  },
};
function getMarginxChainsData(queryPairData) {
  return queryPairData.map((data) => {
    return new ChainInfo({
      chainId: `marginx:${data.chainId}`,
      chainName: data.chainName,
      rpc: data.jsonRpcUrl,
      rest: data.restUrl,
      ...marginxSuggestChainsBase,
    });
  });
}
async function getCosmosAccounts(client, chainId) {
  return await client.request({
    method: "cosmos_getAccounts",
    chainId: chainId,
  });
}
async function getEthAccounts(client, chainId) {
  return await client.request({
    method: "eth_requestAccounts",
    chainId: chainId,
  });
}
function isKeplr(type) {
  return type === "keplr";
}
function isMetamask(type) {
  return type === "metamask";
}

function cleanup() {
  localStorage.removeItem("wc@2:core:0.3//keychain");
  localStorage.removeItem("wc@2:core:0.3//pairing");
  localStorage.removeItem("wc@2:core:0.3//subscription");
  localStorage.removeItem("wc@2:core:0.3//expirer");
  localStorage.removeItem("wc@2:core:0.3//history");
  localStorage.removeItem("wc@2:core:0.3//messages");
  localStorage.removeItem("wc@2:client:0.3//proposal");
  localStorage.removeItem("wc@2:client:0.3//session");
}

export default {
  state: () => ({
    qrImg: "",
    isQrImgLoading: true,
    showMessageBox: false,
    showWalletConnectMessageBox: false,
    showWalletConnectv2MessageBox: false,
    showKeplrMessageBox: false,
    showMetamaskMessageBox: false,
    ownerAddress: "",
    inviteCode: "",
    connectType: "",
  }),
  mutations: {
    SET_CONNECT_TYPE(state, data) {
      state.connectType = data ? data : "";
      data
        ? localStorage.setItem("connectType", data)
        : localStorage.removeItem("connectType");
    },
    SET_NFT_IMG(state, data) {
      state.nftImg = data;
    },
    SET_QR_IMG(state, data) {
      state.qrImg = data;
    },
    SET_QR_IMG_LOADING(state, data) {
      state.isQrImgLoading = data;
    },
    SET_SHOW_MESSAGE_BOX(state, data) {
      state.showMessageBox = data;
    },
    SET_SHOW_WALLET_CONNECT(state, data) {
      state.showWalletConnectMessageBox = data;
    },
    SET_SHOW_WALLET_CONNECT_V2(state, data) {
      state.showWalletConnectMessageBox = data;
    },
    SET_SHOW_KEPLR(state, data) {
      state.showKeplrMessageBox = data;
    },
    SET_SHOW_METAMASK(state, data) {
      state.showMetamaskMessageBox = data;
    },
    SET_OWNER_ADDRESS(state, data) {
      state.ownerAddress = data;
    },
    SET_INVITE_CODE(state, data) {
      state.inviteCode = data;
    },
  },
  actions: {
    async setQR({ commit }, uri) {
      const qr = await QRCode.toDataURL(uri, {
        errorCorrectionLevel: "L",
        margin: 0,
      });

      commit("SET_QR_IMG", qr);
    },
    async walletSwitchEthereumChain({ rootState }, opt) {
      try {
        await rootState.wc.request({
          method: "wallet_switchEthereumChain",
          params: [opt],
        });
      } catch (error) {
        error.code === 4902 &&
          (await rootState.wc.request({
            method: "wallet_addEthereumChain",
            params: [opt],
          }));
      }
    },
    async ethereumSignAndBroadcast({ dispatch, rootState }, data) {
      await dispatch("walletSwitchEthereumChain", {
        chainId: `0x${data.evmChainId.toString(16)}`,
        chainName: data.chainName,
        nativeCurrency: {
          name: SpecialTreatment.getPairRearVal(data.pair),
          symbol: SpecialTreatment.getPairRearVal(data.pair),
          decimals: 18,
        },
        rpcUrls: [data.web3Url],
      });

      const ethereumProvider = await EthereumJsonProvider.init(rootState.wc, {
        chainId: ethereumFxevm.chainId,
      });

      // const cosmosAccounts = await getCosmosAccounts(
      //   rootState.wc,
      //   marginxChain.chainId
      // );

      try {
        const transaction = await buildMetamaskTransfer[data.typeUrl](data);
        console.log("ethereumSignAndBroadcast-transaction", transaction);
        const result = await ethereumProvider.signTxAndBroadcast(transaction);
        return Promise.resolve(result);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async cosmsSignAndBroadcast({ rootState, state, dispatch }, data) {
      console.log("cosmsSignAndBroadcast", data);
      const keplr = isKeplr(state.connectType);

      // keplr &&
      //   cosmosMarginxChain.set({
      //     chainId: `marginx:${data.chainId}`,
      //     chainName: data.chainName,
      //     rpc: data.jsonRpcUrl, //window.location.options + `/rpc/BTC:CUSD`, // data.jsonRpcUrl
      //     rest: data.rest,
      //   });

      // const marginxChain = keplr
      //   ? cosmosMarginxChain
      //   : rootState.wc.chains.find(
      //       (v) => v.chainId === `marginx:${data.chainId}`
      //     );

      let result;

      const ibcTransferMsg = {
        typeUrl: data.typeUrl,
        value: data.value,
      };
      const cosmosProvider = await CosmosJsonProvider.init(rootState.wc, {
        chainId: marginxChainsData.chainId,
        prefix: fxDexTxConfig.options.prefix,
        aminoTypes: fxDexTxConfig.options.aminoTypes,
        registry: fxDexTxConfig.options.registry,
        rpc: marginxChainsData.rpc,
      });
      try {
        if (!keplr) {
          await dispatch("walletSwitchEthereumChain", {
            chainId: `0x${data.evmChainId.toString(16)}`,
            chainName: data.chainName,
            nativeCurrency: {
              name: SpecialTreatment.getPairRearVal(data.pair),
              symbol: SpecialTreatment.getPairRearVal(data.pair),
              decimals: 18,
            },
            rpcUrls: [data.web3Url],
          });
          const account = await cosmosProvider.client.getAccount(data.account);
          const fee = data.fees
            ? data.fees
            : {
                amount: coins("0", SpecialTreatment.getPairRearVal(data.pair)),
                gas: "5000000",
              };
          const toAminoIbcTransferMsg = await EIP712HandleSpecial[data.typeUrl](
            ibcTransferMsg
          );
          console.log("toAminoIbcTransferMsg", toAminoIbcTransferMsg);
          const signDocEIP712 = {
            chain_id: data.chainId,
            account_number: account.accountNumber.toString(),
            sequence: account.sequence.toString(),
            fee,
            msgs: [toAminoIbcTransferMsg],
            memo: "",
          };
          const signDocCosmos = {
            chain_id: data.chainId,
            account_number: account.accountNumber.toString(),
            sequence: account.sequence.toString(),
            fee,
            msgs: [ibcTransferMsg],
            memo: "",
          };
          const typedData = createTypedData(data.evmChainId, signDocEIP712);
          console.log("typedData", typedData);

          result = await ethSignTypedDataAndBroadcast(
            data.account,
            cosmosProvider,
            rootState.wc,
            signDocCosmos,
            typedData
          );
        } else {
          const cosmosAccounts = await getCosmosAccounts(
            rootState.wc,
            marginxChainsData.chainId
          );
          console.log("cosmosAccounts", cosmosAccounts);
          result = await cosmosProvider.signTxAndBroadcast(
            {
              signer: cosmosAccounts[0].bech32Address,
              publicKey: cosmosAccounts[0].publicKey,
              algo: cosmosAccounts[0].algo,
              messages: [ibcTransferMsg],
              mode: "amino",
              fee: data.fees
                ? data.fees
                : {
                    amount: coins(
                      "0",
                      SpecialTreatment.getPairRearVal(data.pair)
                    ),
                    gas: "5000000",
                  },
            },
            {
              preferNoSetFee: true,
              preferNoSetMemo: true,
              disableBalanceCheck: true,
            }
          );
        }
        console.log("result", result);

        return result.code == 0
          ? Promise.resolve(result.transactionHash)
          : Promise.reject({ message: $t(`error.chain[${result.code}]`) });
      } catch (error) {
        console.error("error", error);
        return Promise.reject(error);
      }
    },
    async initConnect({ dispatch, commit, rootState }, opt) {
      const projectId = process.env.VUE_APP_PROJECTID;
      const connector = new Connector({
        walletconnect: {
          projectId,
          bridge: "https://wallet-connect.pundix.com/bridge/",
          modal: {
            async open(uri, cb, rest) {
              await dispatch("setQR", uri);
              commit("SET_QR_IMG_LOADING", false);

              connector.refreshHandler = async () => {
                console.log("<<refresh>>");
                commit("SET_QR_IMG_LOADING", true);
                const uri = await rest();
                console.log("uri", uri);
                await dispatch("setQR", uri);
                commit("SET_QR_IMG_LOADING", false);
              };
            },
            close() {
              console.log("close");
            },
          },
        },
      });
      const metamask = isMetamask(opt.connectType);

      connector.connectWalletDecimals = metamask ? 6 : 6;
      connector.connectType = opt.connectType;

      commit("SAVE_WC", connector);
      console.log("connector", connector);

      dispatch("registerEvent", { connectType: opt.connectType });

      // const queryPairData = await api["queryPair"]({});

      // const marginxChainsData = getMarginxChainsData(queryPairData);

      console.log("marginxChainsData", marginxChainsData);
      console.log("ethereumMxevm", ethereumMxevm);

      const sessions = await rootState.wc.sessions.getSessions();
      console.log("sessions", sessions);

      const keplr = isKeplr(opt.connectType);

      // cosmosMarginxChain.set({
      //   chainId: `marginx:${ConfigureInfo.Marginx.chainId}`,
      //   chainName: ConfigureInfo.Marginx.chainName,
      //   rpc: ConfigureInfo.Marginx.jsonRpcUrl,
      //   rest: ConfigureInfo.Marginx.restUrl,
      // });

      ethereumFxevm.set({
        appConfig: {
          session: 39778,
          target: "fxevm",
          methods: [],
        },
      });

      try {
        await rootState.wc.connect(
          {
            type: opt.connectType,
            session: sessions[0],
          },
          // [ethereumFxevm, ...(keplr ? [cosmosMarginxChain] : marginxChainsData)]
          [ethereumFxevm, ethereumMxevm, marginxChainsData]
        );

        const accounts = keplr
          ? await getCosmosAccounts(rootState.wc, marginxChainsData.chainId)
          : await getEthAccounts(rootState.wc, ethereumMxevm.chainId);
        // const accounts = await getEthAccounts(
        //   rootState.wc,
        //   ethereumMxevm.chainId
        // );
        console.log("accounts", accounts);

        commit("CHANGE_USER_INFO", {
          address: keplr
            ? accounts[0].bech32Address
            : hexAddressToBech32("mx", accounts[0].address), // getAddress(accounts[0].address)
          publicKey: accounts[0].publicKey || "",
          hexAddress: getAddress(accounts[0].address),
          bech32Address: accounts[0].bech32Address || "",
          algo: accounts[0].algo || "",
          name: "",
          chainId: accounts[0].chainId || "",
        });
        commit("SET_CONNECT_TYPE", opt.connectType);
        commit("SET_SHOW_MESSAGE_BOX", false);
      } catch (error) {
        console.error(error);
        throw new Error($t("home.walletConnect.disconnect.message"));
      } finally {
        commit("SET_SHOW_WALLET_CONNECT", false);
        commit("SET_SHOW_KEPLR", false);
        commit("SET_SHOW_METAMASK", false);
      }
    },

    registerEvent({ rootState, commit, dispatch }, opt) {
      const { wc } = rootState;
      const metamask = isMetamask(opt.connectType);

      wc.on("disconnect", async () => {
        console.log("<<disconnect>>");
        commit("CHANGE_USER_INFO", null);
        commit("SET_CONNECT_TYPE", "");
        cleanup();
        // Notification({
        //   title: $t("home.walletConnect.disconnect.title"),
        //   message: $t("home.walletConnect.disconnect.message"),
        //   type: "error",
        //   showClose: true,
        //   duration: 5000,
        // });
      });

      wc.on("accountsChanged", async () => {
        console.log("<<accountsChanged>>");
        dispatch("logoutConnectWallet");
      });

      wc.rewardOfFunc = async (params) => {
        const provider = getProvider(ConfigureInfo.FxEvm.rpc);
        const contract = new WithdrawRewardsContracts(
          provider,
          params.account,
          ConfigureInfo.WithdrawRewardsAddr["FX"]
        );
        const res = await contract.rewardOf(params.account);

        return EthersBigNumber.from(res[0]).toString();
      };

      wc.claimGasFees = async (params) => {
        metamask &&
          (await dispatch("walletSwitchEthereumChain", {
            chainId: `0x${ConfigureInfo.FxEvm.chainId.toString(16)}`,
            chainName: ConfigureInfo.FxEvm.chainName,
            nativeCurrency: ConfigureInfo.FxEvm.nativeCurrency,
            rpcUrls: [ConfigureInfo.FxEvm.rpc],
          }));

        const transaction = await buildContractsTransfer.claim(params);
        const gasPrice =
          transaction.maxFeePerGas || transaction.gasPrice || "1";
        const gasLimit = transaction.gasLimit || "1";

        return Format.setBN({ DECIMAL_PLACES: 18 })
          .init(gasLimit.toString())
          .multiply(gasPrice.toString())
          .div(Math.pow(10, 18))
          .result(false)
          .toString();
      };
      wc.approve = async (receiver, tokenAddr, fxevm, contracts) => {
        metamask &&
          (await dispatch("walletSwitchEthereumChain", {
            chainId: `0x${fxevm.chainId.toString(16)}`,
            chainName: fxevm.chainName,
            nativeCurrency: fxevm.nativeCurrency,
            rpcUrls: [fxevm.rpc],
          }));

        const ethereumProvider = await EthereumJsonProvider.init(wc, {
          chainId: ethereumFxevm.chainId,
        });
        const erc20 = withContract(
          tokenAddr,
          erc20Fragment,
          ethereumProvider.getSigner(receiver)
        );
        const buildTx = await erc20.populateTransaction.approve(
          contracts,
          `0x${"f".repeat(64)}`
        );

        buildTx.from = receiver;

        const transaction = await ethereumProvider
          .getSigner(buildTx.from)
          .populateTransaction(buildTx);

        let hash;

        if (metamask) {
          hash = await ethereumProvider.signTxAndBroadcast(transaction);
        } else {
          const signedTransaction = await ethereumProvider.signTx(transaction);
          const provider = getProvider(ethereumFxevm.rpc);
          const broadcastTxResult = await provider.sendTransaction(
            signedTransaction
          );
          hash = broadcastTxResult.hash;
        }
        const provider = getProvider(fxevm.rpc);

        return await provider.waitForTransaction(hash);
      };

      wc.allowance = (receiver, tokenAddr, rpc, contracts) => {
        const provider = getProvider(rpc);
        const erc20 = withContract(tokenAddr, erc20Fragment, provider);

        return erc20.allowance(receiver, contracts);
      };

      wc.getChainBalance = async (
        opt,
        chain = "",
        decimals = 6,
        balance = 0
      ) => {
        console.log(
          "getChainBalance",
          opt,
          (chain = ""),
          (decimals = 6),
          (balance = 0)
        );
        const fxcoreChain = "fxcore",
          fxPrefix = "FX";

        try {
          const provider = getProvider(opt.web3Url);
          const isTokenAddrProvided = Boolean(opt.tokenAddr);

          if (!metamask) {
            if (chain === fxcoreChain) {
              if (isTokenAddrProvided) {
                const contract = await withContract(
                  opt.tokenAddr,
                  erc20Fragment,
                  provider
                );
                balance = await contract.balanceOf(opt.address);
                decimals = await contract.decimals();
              } else {
                balance = await provider.getBalance(opt.address);
                decimals = 18;
              }

              balance = balance.toString();
            } else {
              const client = await SigningFxClient.connect(opt.jsonRpcUrl);
              balance = await client.getBalance(opt.bech32Address, opt.prefix);
              balance = balance.amount;
            }
          } else {
            if (opt.prefix === fxPrefix) {
              const client = await SigningFxClient.connect(opt.jsonRpcUrl);
              balance = await client.getBalance(opt.bech32Address, opt.prefix);
              balance = balance.amount;
            } else {
              if (isTokenAddrProvided) {
                const contract = await withContract(
                  opt.tokenAddr,
                  erc20Fragment,
                  provider
                );
                balance = await contract.balanceOf(opt.address);
                decimals = await contract.decimals();
              } else {
                balance = await provider.getBalance(opt.address);
                decimals = 18;
              }

              balance = balance.toString();
            }
          }
        } catch (error) {
          console.error(error);
          throw new Error("Failed to get chain balance.");
        }

        return {
          decimals: decimals,
          balance: balance,
        };
      };

      wc.crossChainGasFees = async (data) => {
        metamask &&
          (await dispatch("walletSwitchEthereumChain", {
            chainId: `0x${data.evmChainId.toString(16)}`,
            chainName: data.chainName,
            nativeCurrency: {
              name: "FX",
              symbol: "FX",
              decimals: 18,
            },
            rpcUrls: [data.web3Url],
          }));

        const transaction = await buildContractsTransfer.crossChain(data);
        const gasPrice =
          transaction.maxFeePerGas || transaction.gasPrice || "1";
        const gasLimit = transaction.gasLimit || "1";

        return Format.setBN({ DECIMAL_PLACES: 18 })
          .init(gasLimit.toString())
          .multiply(gasPrice.toString())
          .div(10 ** 18)
          .result(false)
          .toString();
      };

      wc.crossChainSignAndBroadcast = async (data) => {
        console.log("crossChainSignAndBroadcast", data);
        const ethereumProvider = await EthereumJsonProvider.init(wc, {
          chainId: ethereumFxevm.chainId,
        });
        const transaction = await buildContractsTransfer.crossChain(data);

        if (metamask) {
          return await ethereumProvider.signTxAndBroadcast(transaction);
        } else {
          const signedTransaction = await ethereumProvider.signTx(transaction);
          const provider = getProvider(ethereumFxevm.rpc);
          const broadcastTxResult = await provider.sendTransaction(
            signedTransaction
          );

          return broadcastTxResult.hash;
        }
      };

      wc.claimSignAndBroadcast = async (data) => {
        const ethereumProvider = await EthereumJsonProvider.init(wc, {
          chainId: ethereumFxevm.chainId,
        });
        const transaction = await buildContractsTransfer.claim(data);

        if (metamask) {
          return await ethereumProvider.signTxAndBroadcast(transaction);
        } else {
          const signedTransaction = await ethereumProvider.signTx(transaction);
          const provider = getProvider(ethereumFxevm.rpc);
          const broadcastTxResult = await provider.sendTransaction(
            signedTransaction
          );

          return broadcastTxResult.hash;
        }
      };

      wc.signAndBroadcast = async (data) => {
        // return metamask
        //   ? await dispatch("ethereumSignAndBroadcast", data)
        //   : await dispatch("cosmsSignAndBroadcast", data);
        return await dispatch("cosmsSignAndBroadcast", data);
      };
      wc.ordinarySign = async (data) => {
        const ethereumProvider = await EthereumJsonProvider.init(wc, {
          chainId: ethereumFxevm.chainId,
        });
        const signature = await ethereumProvider.signMessage({
          message: JSON.stringify(data.message),
          address: data.signer,
          chainId: ethereumFxevm.chainId,
        });
        console.log("ordinarySign-signature", signature);
        return {
          signed: JSON.stringify(data.message),
          signature: signature,
        };
      };
    },

    async logoutConnectWallet({ rootState, commit }) {
      try {
        await rootState.wc.disconnect();
      } catch (error) {
        if (!/No matching key/i.test(error.message)) throw error;
      } finally {
        commit("CHANGE_USER_INFO", null);
        commit("SET_CONNECT_TYPE", "");
        cleanup();
        Notification({
          title: $t("home.walletConnect.disconnect.title"),
          message: $t("home.walletConnect.disconnect.message"),
          type: "error",
          showClose: true,
          duration: 5000,
        });
      }
    },
    async openConnectWallet({ commit, rootState }) {
      if (!rootState.user.address || !rootState.connectType)
        commit("SET_SHOW_MESSAGE_BOX", true);
    },
  },
  getters: {
    getConnectType: (state) => {
      const data = localStorage.getItem("connectType");

      state.connectType = data ? data : "";
      data
        ? localStorage.setItem("connectType", data)
        : localStorage.removeItem("connectType");
      return state.connectType;
    },
  },
};
